/**
 * SessionRevocationCard · v2.7.65+E73
 * ══════════════════════════════════════════════════════════════════════
 * Live list of active JWT sessions across every portal (driver / admin /
 * customer). Every issued token carries a `jti` — one click on Revoke
 * adds that jti to the block-list, and the next request from that device
 * fails with 401. Also supports "Revoke ALL for user" for lost-phone
 * scenarios.
 *
 * Polls every 60 s (pausable). Groups rows by sub → user, so the admin
 * sees "Alice has 3 sessions" instead of a flat blur.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { KeyRound, RefreshCw, Loader2, Ban, Smartphone, ShieldOff } from "lucide-react";

const POLL_MS = 60_000;

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function KindBadge({ kind }) {
  const map = {
    admin:    "bg-rose-500/15 text-rose-300 border-rose-500/40",
    driver:   "bg-amber-500/15 text-amber-300 border-amber-500/40",
    customer: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    business: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  };
  const cls = map[kind] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/40";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${cls}`}>
      {kind || "unknown"}
    </span>
  );
}

export default function SessionRevocationCard() {
  const [rows, setRows] = useState([]);
  const [counters, setCounters] = useState({ active: 0, revoked: 0 });
  const [loading, setLoading] = useState(false);
  const [autoPoll, setAutoPoll] = useState(true);
  const [revokingJti, setRevokingJti] = useState(null);
  const [revokingSub, setRevokingSub] = useState(null);
  const lastLoadRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/sessions?active_only=true&limit=200`);
      setRows(data.items || []);
      setCounters(data.counters || { active: 0, revoked: 0 });
      lastLoadRef.current = new Date();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Session load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoPoll) return undefined;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [autoPoll, load]);

  const revoke = useCallback(async (row) => {
    if (!row?.jti) return;
    const reason = window.prompt("Reason (audit-logged, optional):", "manual") || "manual";
    setRevokingJti(row.jti);
    try {
      await adminApi.post(`/admin/sessions/${row.jti}/revoke`, null, { params: { reason } });
      toast.success(`Session revoked · ${row.display_name || row.sub}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally {
      setRevokingJti(null);
    }
  }, [load]);

  const revokeAll = useCallback(async (sub, name) => {
    if (!sub) return;
    if (!window.confirm(`Revoke ALL active sessions for ${name || sub}? Every device they're signed into will be signed out at their next request.`)) return;
    const reason = window.prompt("Reason (audit-logged, optional):", "lost-device") || "lost-device";
    setRevokingSub(sub);
    try {
      const { data } = await adminApi.post(`/admin/sessions/revoke-all/${encodeURIComponent(sub)}`, null, { params: { reason } });
      toast.success(`Revoked ${data.revoked_count} session(s) for ${name || sub}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke-all failed");
    } finally {
      setRevokingSub(null);
    }
  }, [load]);

  // Group by sub so the UI shows "Alice (3 sessions)" cards.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.sub || "unknown";
      if (!map.has(key)) map.set(key, { sub: key, display_name: r.display_name, display_email: r.display_email, display_role: r.display_role, sessions: [] });
      map.get(key).sessions.push(r);
    }
    return Array.from(map.values());
  }, [rows]);

  const summaryLine = useMemo(() => {
    const parts = [];
    parts.push(`${counters.active} active`);
    parts.push(`${counters.revoked} revoked (all-time)`);
    if (lastLoadRef.current) parts.push(`checked ${lastLoadRef.current.toLocaleTimeString()}`);
    return parts.join(" · ");
  }, [counters]);

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="session-revocation-card">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-amber-400"/>
          <div>
            <h3 className="font-bold text-sm">Active Sessions</h3>
            <div className="text-[10px] text-zinc-500 mt-0.5" data-testid="session-summary">{summaryLine}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Badge className="bg-zinc-500/15 text-zinc-300 border-zinc-500/40 text-[10px] uppercase tracking-wider" data-testid="session-active-chip">
            {counters.active} active
          </Badge>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="session-autopoll-toggle">
            <Switch checked={autoPoll} onCheckedChange={setAutoPoll}/>
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-300">Auto · 60 s</span>
          </label>
          <Button
            onClick={load}
            disabled={loading}
            variant="outline"
            className="h-8 border-zinc-700 hover:bg-zinc-800 text-xs"
            data-testid="session-refresh"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin"/> : <RefreshCw className="h-3 w-3"/>}
            <span className="ml-1">Refresh</span>
          </Button>
        </div>
      </div>

      {grouped.length === 0 && !loading && (
        <div className="rounded border border-zinc-800 p-6 text-center text-zinc-500 text-xs" data-testid="session-empty">
          No active sessions right now.
        </div>
      )}

      <div className="space-y-3">
        {grouped.map((g) => (
          <div key={g.sub} className="rounded border border-zinc-800 bg-zinc-950/40 p-3" data-testid={`session-user-${g.sub}`}>
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="font-semibold text-zinc-100 flex items-center gap-2 truncate">
                  <Smartphone className="h-3.5 w-3.5 text-zinc-400 flex-shrink-0"/>
                  <span className="truncate" title={g.display_email || g.sub}>{g.display_name || g.sub}</span>
                </div>
                <div className="text-[10px] text-zinc-500 truncate">
                  {g.display_email || g.sub} {g.display_role ? `· ${g.display_role}` : ""} · {g.sessions.length} session{g.sessions.length === 1 ? "" : "s"}
                </div>
              </div>
              <Button
                onClick={() => revokeAll(g.sub, g.display_name)}
                disabled={revokingSub === g.sub}
                className="h-7 px-2.5 bg-rose-600 hover:bg-rose-500 text-white text-[10px] font-bold flex-shrink-0"
                data-testid={`session-revoke-all-${g.sub}`}
              >
                {revokingSub === g.sub ? <Loader2 className="h-3 w-3 animate-spin"/> : <ShieldOff className="h-3 w-3"/>}
                <span className="ml-1">Revoke ALL</span>
              </Button>
            </div>
            <div className="rounded border border-zinc-800/80 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-zinc-900/60 text-[9px] uppercase tracking-widest text-zinc-500">
                  <tr>
                    <th className="text-left px-2 py-1">Kind</th>
                    <th className="text-left px-2 py-1">Issued</th>
                    <th className="text-left px-2 py-1">Expires</th>
                    <th className="text-left px-2 py-1">Remember</th>
                    <th className="text-left px-2 py-1">Token ID (last 8)</th>
                    <th className="text-right px-2 py-1">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/70">
                  {g.sessions.map((s) => (
                    <tr key={s.jti} className="hover:bg-zinc-950/60" data-testid={`session-row-${s.jti}`}>
                      <td className="px-2 py-1"><KindBadge kind={s.kind}/></td>
                      <td className="px-2 py-1 text-zinc-400 font-mono">{fmtTs(s.issued_at)}</td>
                      <td className="px-2 py-1 text-zinc-400 font-mono">{fmtTs(s.expires_at)}</td>
                      <td className="px-2 py-1">
                        {s.remember ? (
                          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 text-[9px] uppercase">30-day</Badge>
                        ) : (
                          <span className="text-[10px] text-zinc-500">short</span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-zinc-500 font-mono text-[10px]">…{(s.jti || "").slice(-8)}</td>
                      <td className="px-2 py-1 text-right">
                        <Button
                          onClick={() => revoke(s)}
                          disabled={revokingJti === s.jti}
                          className="h-6 px-2 bg-rose-600 hover:bg-rose-500 text-white text-[10px] font-bold"
                          data-testid={`session-revoke-${s.jti}`}
                        >
                          {revokingJti === s.jti ? <Loader2 className="h-2.5 w-2.5 animate-spin"/> : <Ban className="h-2.5 w-2.5"/>}
                          <span className="ml-1">Revoke</span>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
